import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  InputAdornment,
  TextField,
  Typography,
  Box,
  Chip,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { Edit, DeleteOutline } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import PublicIcon from "@material-ui/icons/Public";
import PersonIcon from "@material-ui/icons/Person";
import AddIcon from "@material-ui/icons/Add";
import FlashOnIcon from "@material-ui/icons/FlashOn";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import QuickAnswersModal from "../../components/QuickAnswersModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { poderDeAdmin } from "../../utils/perfil";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICK_ANSWERS") {
    const quickAnswers = action.payload;
    const novos = [];

    quickAnswers.forEach((quickAnswer) => {
      const idx = state.findIndex((q) => q.id === quickAnswer.id);
      if (idx !== -1) {
        state[idx] = quickAnswer;
      } else {
        novos.push(quickAnswer);
      }
    });

    return [...state, ...novos];
  }

  if (action.type === "UPDATE_QUICK_ANSWERS") {
    const quickAnswer = action.payload;
    const idx = state.findIndex((q) => q.id === quickAnswer.id);

    if (idx !== -1) {
      state[idx] = quickAnswer;
      return [...state];
    }
    return [quickAnswer, ...state];
  }

  if (action.type === "DELETE_QUICK_ANSWERS") {
    const quickAnswerId = action.payload;
    const idx = state.findIndex((q) => q.id === quickAnswerId);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }

  if (action.type === "RESET") return [];

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: 16,
    border: "none",
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 6px -1px rgba(0,0,0,0.2)"
        : "0 4px 6px -1px rgba(0,0,0,0.05)",
  },
  searchInput: {
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f9fafb",
    borderRadius: 10,
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#e4e7ec"
    }`,
    padding: "4px 12px",
    "& .MuiInput-underline:before": { display: "none" },
    "& .MuiInput-underline:after": { display: "none" },
    "& input": { padding: "8px 0", fontSize: "0.9rem" },
  },
  filtros: {
    display: "flex",
    gap: 8,
    marginBottom: 18,
    flexWrap: "wrap",
  },
  filtroChip: {
    borderRadius: 8,
    fontWeight: 600,
  },
  grade: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 14,
  },
  card: {
    position: "relative",
    borderRadius: 14,
    padding: "16px 18px",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#fff",
    transition: "all .18s ease",
    "&:hover": {
      borderColor: "rgba(30,152,85,0.45)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 18px -8px rgba(0,0,0,0.18)",
    },
    "&:hover $acoes": { opacity: 1 },
  },
  cardTopo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 10,
  },
  atalho: {
    fontFamily: "monospace",
    fontWeight: 700,
    fontSize: "1rem",
    padding: "3px 10px",
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(30,152,85,0.18)"
        : "rgba(30,152,85,0.10)",
    color: theme.palette.type === "dark" ? "#7ee2a8" : "#14713f",
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  mensagem: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    whiteSpace: "pre-wrap",
    minHeight: 60,
  },
  rodape: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 12,
    gap: 8,
  },
  escopoChip: {
    height: 22,
    fontSize: "0.7rem",
    fontWeight: 600,
    borderRadius: 6,
  },
  acoes: {
    display: "flex",
    gap: 2,
    opacity: 0.35,
    transition: "opacity .18s ease",
  },
  actionButton: {
    color: theme.palette.text.secondary,
    "&:hover": { color: theme.palette.text.primary },
  },
  vazio: {
    textAlign: "center",
    padding: "60px 20px",
    color: theme.palette.text.secondary,
  },
  vazioIcone: {
    fontSize: 46,
    opacity: 0.35,
    marginBottom: 8,
  },
}));

const QuickAnswers = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const ehAdmin = poderDeAdmin(user?.profile);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [filtro, setFiltro] = useState("todas"); // todas | global | minhas
  const [quickAnswers, dispatch] = useReducer(reducer, []);
  const [selectedQuickAnswers, setSelectedQuickAnswers] = useState(null);
  const [quickAnswersModalOpen, setQuickAnswersModalOpen] = useState(false);
  const [deletingQuickAnswers, setDeletingQuickAnswers] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => {
      const fetchQuickAnswers = async () => {
        try {
          const { data } = await api.get("/quickAnswers/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_QUICK_ANSWERS", payload: data.quickAnswers });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchQuickAnswers();
    }, 400);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    const onQuickanswer = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICK_ANSWERS", payload: data.quickAnswer });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICK_ANSWERS", payload: +data.quickAnswerId });
      }
    };
    socket.on("quickAnswer", onQuickanswer);

    return () => socket.off("quickAnswer", onQuickanswer);
  }, []);

  const handleSearch = (event) => setSearchParam(event.target.value.toLowerCase());

  const handleOpenQuickAnswersModal = () => {
    setSelectedQuickAnswers(null);
    setQuickAnswersModalOpen(true);
  };

  const handleCloseQuickAnswersModal = () => {
    setSelectedQuickAnswers(null);
    setQuickAnswersModalOpen(false);
  };

  const handleEditQuickAnswers = (quickAnswer) => {
    setSelectedQuickAnswers(quickAnswer);
    setQuickAnswersModalOpen(true);
  };

  const handleDeleteQuickAnswers = async (quickAnswerId) => {
    try {
      await api.delete(`/quickAnswers/${quickAnswerId}`);
      toast.success("Resposta rápida excluída.");
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickAnswers(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  // Só mexe no que é seu: admin mexe em tudo, atendente só nas próprias.
  const podeMexer = (quickAnswer) =>
    ehAdmin || (quickAnswer.userId && +quickAnswer.userId === +user?.id);

  const visiveis = quickAnswers.filter((q) => {
    if (filtro === "global") return !q.userId;
    if (filtro === "minhas") return +q.userId === +user?.id;
    return true;
  });

  return (
    <MainContainer onScroll={handleScroll}>
      <ConfirmationModal
        title={
          deletingQuickAnswers &&
          `Excluir a resposta rápida ${deletingQuickAnswers.shortcut}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickAnswers(deletingQuickAnswers.id)}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <QuickAnswersModal
        open={quickAnswersModalOpen}
        onClose={handleCloseQuickAnswersModal}
        aria-labelledby="form-dialog-title"
        quickAnswerId={selectedQuickAnswers && selectedQuickAnswers.id}
      />

      <MainHeader>
        <Title>Respostas Rápidas</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Pesquisar atalho ou mensagem..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenQuickAnswersModal}
          >
            Nova resposta
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <Box className={classes.filtros}>
          {[
            { id: "todas", label: "Todas" },
            { id: "global", label: "Globais" },
            { id: "minhas", label: "Minhas" },
          ].map((f) => (
            <Chip
              key={f.id}
              label={f.label}
              className={classes.filtroChip}
              color={filtro === f.id ? "primary" : "default"}
              variant={filtro === f.id ? "default" : "outlined"}
              onClick={() => setFiltro(f.id)}
              size="small"
            />
          ))}
        </Box>

        {!loading && visiveis.length === 0 && (
          <Box className={classes.vazio}>
            <FlashOnIcon className={classes.vazioIcone} />
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              Nenhuma resposta rápida por aqui
            </Typography>
            <Typography variant="body2">
              Crie um atalho como <strong>/</strong> e escreva a mensagem que
              você mais repete.
            </Typography>
          </Box>
        )}

        <Box className={classes.grade}>
          {visiveis.map((quickAnswer) => (
            <Box key={quickAnswer.id} className={classes.card}>
              <Box className={classes.cardTopo}>
                <span className={classes.atalho}>{quickAnswer.shortcut}</span>
              </Box>

              <Typography className={classes.mensagem}>
                {quickAnswer.message}
              </Typography>

              <Box className={classes.rodape}>
                {quickAnswer.userId ? (
                  <Chip
                    size="small"
                    icon={<PersonIcon style={{ fontSize: 14 }} />}
                    label={
                      +quickAnswer.userId === +user?.id
                        ? "Minha"
                        : quickAnswer.user?.name || "Pessoal"
                    }
                    className={classes.escopoChip}
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    size="small"
                    icon={<PublicIcon style={{ fontSize: 14 }} />}
                    label="Global"
                    className={classes.escopoChip}
                    color="primary"
                    variant="outlined"
                  />
                )}

                {podeMexer(quickAnswer) && (
                  <Box className={classes.acoes}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        className={classes.actionButton}
                        onClick={() => handleEditQuickAnswers(quickAnswer)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        className={classes.actionButton}
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingQuickAnswers(quickAnswer);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress size={26} />
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default QuickAnswers;
